/* eslint-disable no-use-before-define */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { API_PATHS, GdprUtils, apiClient } from '@cyberint/common-utils';
import { Modal } from '@cyberint/dsc';
import { FORCE_LOGOUT_EVENT } from '../../constants';
import { t } from '../../i18n';
import { resetUnauthorizedThrottle } from '../../utils/unauthorized-interceptor';

const MINUTE_IN_SECONDS = 60;
const SECOND_IN_MILLIS = 1000;
const INITIAL_CHECK_DELAY_MILLIS = 500;
const NOT_AUTHORIZED = 401;

const EXPIRED_SESSION = 'expiredSession';
const NOT_EXPIRED_SESSION = 'notExpiredSession';

const noop = () => {};

// Same region-prefixed /me URL apps/ui's lib/http GDPR interceptor produces
// (getRegion falls back to 'us', so the prefix is always applied)
const fetchMe = config =>
  apiClient.defaultClient.get(
    GdprUtils.modifyUrlWithPrefix(`${API_PATHS.API_V1}/me`, GdprUtils.getRegion(), API_PATHS.API_V1),
    config
  );

const mapModal = {
  [EXPIRED_SESSION]: {
    title: () => t('session-expiration-modal.expired.title'),
    message: () => t('session-expiration-modal.expired.message'),
    actionOk: {
      text: () => t('session-expiration-modal.login'),
      handlerFunctionName: 'onLogout'
    }
  },
  [NOT_EXPIRED_SESSION]: {
    title: () => t('session-expiration-modal.not-expired.title'),
    message: (seconds = 0) => t('session-expiration-modal.not-expired.message', { seconds }),
    actionOk: {
      text: () => t('session-expiration-modal.keep-working'),
      handlerFunctionName: 'onKeepWorking'
    },
    actionCancel: {
      text: () => t('session-expiration-modal.logout'),
      handlerFunctionName: 'onLogout'
    }
  }
};

/**
 * SessionTimeoutDialogBase - requesting session TTL via /me.
 * If TTL is less or equal 1 minute the modal window will show countdown.
 * When countdown reaches 0 - modal will show the expired message.
 * Also listens for FORCE_LOGOUT_EVENT (dispatched by the 401 unauthorized-interceptor)
 * and shows the expired modal.
 *
 * Store-agnostic: the host app provides onSignOut (and optionally onKeepWorking)
 * to clear its own state (Redux in apps/ui, Jotai atoms in ui-shell).
 *
 * For details check ANG-5770 (https://jira.dev-services.cyberint.io/browse/ANG-5770)
 */
const SessionTimeoutDialogBase = ({ onSignOut, onKeepWorking }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const modalTypeRef = useRef(null);
  const checkTimeoutRef = useRef(null);
  const countdownTimeoutRef = useRef(null);

  const setModalTypeSynced = type => {
    modalTypeRef.current = type;
    setModalType(type);
  };

  const clearAllTimeouts = () => {
    clearTimeout(checkTimeoutRef.current);
    clearTimeout(countdownTimeoutRef.current);
  };

  const showExpiredModal = () => {
    clearTimeout(checkTimeoutRef.current);
    setModalTypeSynced(EXPIRED_SESSION);
    setIsVisible(true);
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'User session expired',
      level: 'info'
    });
  };

  const showAboutToExpireModal = secondsLeft => {
    clearTimeout(checkTimeoutRef.current);
    setModalTypeSynced(NOT_EXPIRED_SESSION);
    setIsVisible(true);
    startCountdown(secondsLeft);
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'User session about to expire',
      level: 'info'
    });
  };

  const startCountdown = sec => {
    clearTimeout(countdownTimeoutRef.current);

    if (sec === 0) {
      setModalTypeSynced(EXPIRED_SESSION);
    }

    if (sec >= 0) {
      setSeconds(sec);

      countdownTimeoutRef.current = setTimeout(() => {
        startCountdown(sec - 1);
      }, SECOND_IN_MILLIS);
    }
  };

  const onDataReceived = resp => {
    const timeDelta = resp?.data?.response?.expiry_in_seconds;

    if (timeDelta == null) return;

    if (timeDelta <= 0) {
      showExpiredModal();
      return;
    }

    if (timeDelta <= MINUTE_IN_SECONDS) {
      showAboutToExpireModal(Math.floor(timeDelta));
      return;
    }

    scheduleNextCheck((timeDelta - MINUTE_IN_SECONDS) * SECOND_IN_MILLIS);
  };

  const onErrorReceived = err => {
    const isUnauthorized = (err?.status || err?.response?.status) === NOT_AUTHORIZED;

    if (isUnauthorized) showExpiredModal();
  };

  // The dialog handles its own 401 (expired modal) - skip the generic 401 throttle
  const checkSession = () => fetchMe({ skipUnauthorizedInterceptor: true }).then(onDataReceived).catch(onErrorReceived);

  const scheduleNextCheck = delayMillis => {
    clearTimeout(checkTimeoutRef.current);
    checkTimeoutRef.current = setTimeout(checkSession, delayMillis);
  };

  useEffect(() => {
    // Mounted means logged in - re-arm the 401 throttle for this session
    resetUnauthorizedThrottle();
    scheduleNextCheck(INITIAL_CHECK_DELAY_MILLIS);

    const onTabVisibilityChanged = () => {
      if (document.hidden) {
        // if modal still is not active - clear timeouts
        if (!modalTypeRef.current) clearAllTimeouts();
      } else {
        checkSession();
      }
    };

    const onForceLogout = () => {
      clearAllTimeouts();
      showExpiredModal();
    };

    document.addEventListener('visibilitychange', onTabVisibilityChanged);
    window.addEventListener(FORCE_LOGOUT_EVENT, onForceLogout);
    return () => {
      clearAllTimeouts();
      document.removeEventListener('visibilitychange', onTabVisibilityChanged);
      window.removeEventListener(FORCE_LOGOUT_EVENT, onForceLogout);
    };
  }, []);

  const handleKeepWorking = () => {
    setIsVisible(false);
    setModalTypeSynced(null);
    clearAllTimeouts();
    // Authenticated /me call renews the session server-side and reschedules polling
    checkSession();
    onKeepWorking?.();
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'User chose to keep working',
      level: 'info'
    });
  };

  const handleLogout = () => {
    setIsVisible(false);
    clearAllTimeouts();
    onSignOut();
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'User chose to log out in the session expiration dialog',
      level: 'info'
    });
  };

  const actionHandlersMap = {
    onLogout: handleLogout,
    onKeepWorking: handleKeepWorking
  };

  const modalConfig = mapModal[modalType];

  return (
    <Modal
      size={{ width: '500px', height: '250px' }}
      isOpen={isVisible}
      title={modalConfig?.title()}
      testId="session-timeout-dialog"
      okText={modalConfig?.actionOk.text()}
      cancelText={modalConfig?.actionCancel?.text() || null}
      onClose={actionHandlersMap[modalConfig?.actionCancel?.handlerFunctionName] || handleLogout}
      onOk={actionHandlersMap[modalConfig?.actionOk?.handlerFunctionName] || noop}
      content={modalConfig?.message(seconds)}
    />
  );
};

SessionTimeoutDialogBase.propTypes = {
  onSignOut: PropTypes.func.isRequired,
  onKeepWorking: PropTypes.func
};

export { SessionTimeoutDialogBase };
